// photoBatches — how many photos go in ONE request, declared once.
//
// A crew member photographing a job reported "can't upload more than 10 at a
// time" (2026-09-13). The wall was real, and the reason it was a wall rather
// than a pause is that NOTHING on the client knew the limit existed: every
// screen that uploads built one FormData out of whatever the picker returned
// and posted it, so the answer to "I picked 30" was a single refusal with
// nothing uploaded.
//
// Raising the server's number alone does not fix that, because the number that
// actually bounds a request is not the server's:
//
//   nginx  client_max_body_size 50M   deploy.sh:670, :747
//                                     scripts/setup-ssl.sh:142
//
// That is a HARD ceiling on the whole request body, it is enforced before
// Express ever sees the bytes, and it cannot be changed from this repo — see
// the note on NGINX_MAX_REQUEST_BYTES below. Twelve files of 25MB is 300MB, so
// no server-side count can make a big enough batch work. The only thing that
// removes the wall is CHUNKING: the selection has no practical limit, one
// REQUEST does, and the client is the only side that knows each file's size
// before it is sent.
//
// ── The three numbers, and the arithmetic under each ────────────────────────
//
// MAX_BYTES_PER_FILE (25MB) — the same `fileSize` server/routes/media.js gives
//   multer. A 12MP phone JPEG is ~3-5MB and a 50MP one ~8-12MB, so this clears
//   every camera a crew carries. Checked here so a photo too big to ever be
//   accepted is named on the phone instead of being pushed up a yard
//   connection and refused at the far end.
//
// MAX_BYTES_PER_REQUEST (25MB) — deliberately the SAME number, which makes
//   "any one legal photo always fits in a request on its own" true by
//   construction rather than by a second calculation that could drift. It is
//   also half of nginx's 50M, so multipart boundaries, field parts and header
//   overhead have an entire 25MB of slack before a 413 becomes possible.
//
// MAX_FILES_PER_REQUEST (12) — the same MAX_FILES_PER_UPLOAD as media.js, and
//   photoBatches.test.js reads that file to prove it. 12 is where the count
//   stops being the binding constraint: the smallest photo a modern phone
//   produces is ~2MB (HEIC, 12MP), and 25MB / 2MB is about 12. Below that the
//   count would bite on small photos for no reason; above it the byte budget
//   always bites first.
//
// So 30 photos at a typical 4MB is ceil(30 * 4 / 25) = 5 requests, and 100
// photos is 16. Nobody is asked to pick their photos in tens.
//
// ── What this deliberately does NOT change ──────────────────────────────────
//
// The FIELD path. apps/field/actions.js uploads a crew member's photos ONE PER
// REQUEST, and that is not an oversight to be batched away: each photo carries
// its own `client_key`, which server/utils/clientOps.js uses to make a retry
// idempotent, and each is independently queueable by offline/photos.js when
// there is no signal. Batching would put twelve photos behind one key, so a
// lost answer would re-send all twelve or none, and a queue entry would be
// twelve photos that succeed or fail together. One photo per request is
// already unbounded by count; what it needed was to stop abandoning the rest
// of the selection when one is refused, which is in actions.js.

import { describeError } from './http';

export const MAX_FILES_PER_REQUEST = 12;
export const MAX_BYTES_PER_FILE = 25 * 1024 * 1024;
export const MAX_BYTES_PER_REQUEST = 25 * 1024 * 1024;

/**
 * nginx's cap on the whole request body. NOT ENFORCED HERE, and not something
 * this repo can change: it is written by deploy.sh and scripts/setup-ssl.sh
 * into the droplet's nginx config, which a human deploys.
 *
 * It is declared anyway because it is the number the two above have to stay
 * under, and photoBatches.test.js reads it out of deploy.sh — so if somebody
 * lowers it there, the client's budget stops being provably safe and a test
 * says so instead of a crew member finding out with a 413 that carries an
 * nginx HTML page rather than a sentence.
 */
export const NGINX_MAX_REQUEST_BYTES = 50 * 1024 * 1024;

const sizeOf = (f) => {
  const n = Number(f && f.size);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const nameOf = (f) => (f && f.name) || 'a photo';

/** One sentence, ending once. The server's sentences already end in a stop. */
const endSentence = (s) => {
  const t = String(s || '').trim();
  return !t || /[.!?]$/.test(t) ? t : `${t}.`;
};

/** MB, for a sentence. 26214400 -> "25MB"; 1572864 -> "1.5MB". */
export function mb(bytes) {
  const v = bytes / (1024 * 1024);
  return `${v >= 10 || Number.isInteger(v) ? Math.round(v) : v.toFixed(1)}MB`;
}

/**
 * Files this app can never get accepted, separated from the ones it can.
 *
 * A file over MAX_BYTES_PER_FILE is refused by multer at the far end with
 * "File too large" AFTER the whole thing has been pushed up — which on a yard
 * connection is minutes of a crew member's time for a guaranteed no. Naming it
 * here costs nothing: `File.size` is already known.
 */
export function splitOversized(files, max = MAX_BYTES_PER_FILE) {
  const all = Array.from(files || []);
  return {
    sendable: all.filter((f) => sizeOf(f) <= max),
    oversized: all.filter((f) => sizeOf(f) > max),
  };
}

/**
 * Split a selection into requests, in order, bounded by BOTH count and bytes.
 *
 * Order is preserved and no file is dropped or duplicated — photoBatches.test
 * asserts both, because a batcher that loses one photo is worse than the wall
 * it replaced: the crew member is told it worked.
 *
 * A file larger than the byte budget still gets its own request rather than an
 * empty batch or an infinite loop. With the two budgets equal that cannot
 * happen for a file that passed splitOversized, but the guard is cheap and the
 * two numbers are separate parameters.
 */
export function batchFiles(files, { maxFiles = MAX_FILES_PER_REQUEST, maxBytes = MAX_BYTES_PER_REQUEST } = {}) {
  const all = Array.from(files || []);
  const batches = [];
  let current = [];
  let bytes = 0;
  for (const f of all) {
    const size = sizeOf(f);
    const wouldExceed = current.length > 0 && (current.length >= maxFiles || bytes + size > maxBytes);
    if (wouldExceed) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(f);
    bytes += size;
  }
  if (current.length) batches.push(current);
  return batches;
}

/**
 * Send a selection as however many requests it takes.
 *
 * `send(batch, { from, to })` does one request — the caller owns the FormData
 * and the path, because the job route and the standalone route differ in both.
 * `from`/`to` are indices into the SENDABLE list, so a caller carrying
 * per-file captions can slice them.
 *
 * Never throws. It returns what actually happened, because the one outcome
 * this whole change exists to prevent is a partial upload that reads as a
 * success — 30 picked, 12 stored, and a green tick.
 */
export async function uploadInBatches(files, send, { onProgress } = {}) {
  const { sendable, oversized } = splitOversized(files);
  const batches = batchFiles(sendable);
  const out = { sent: 0, total: sendable.length, picked: Array.from(files || []).length, oversized, error: null, results: [] };
  let from = 0;
  if (onProgress) onProgress({ sent: 0, total: out.total });
  for (const batch of batches) {
    try {
      const res = await send(batch, { from, to: from + batch.length });
      out.results.push(res);
      out.sent += batch.length;
      from += batch.length;
      if (onProgress) onProgress({ sent: out.sent, total: out.total });
    } catch (e) {
      out.error = e;
      break;
    }
  }
  return out;
}

/**
 * What a person is told afterwards. `null` means "nothing went wrong" and the
 * caller may say whatever it normally says.
 *
 * Every branch names a COUNT, because "some photos did not upload" is the
 * sentence that sent a crew member back to the yard.
 */
export function uploadSummary(out) {
  if (!out) return null;
  const { sent, total, oversized = [], error } = out;
  const parts = [];

  if (error) {
    const remaining = total - sent;
    // A 413 is NGINX, not this app: the request body went over
    // client_max_body_size, so node never saw it, the body is an HTML error
    // page, and describeError would say "Request failed (413)" — which tells a
    // crew member nothing and looks like the app is broken.
    const why = endSentence(error.status === 413
      ? `That upload was too large for the server to accept — the limit is ${mb(NGINX_MAX_REQUEST_BYTES)} per request`
      : describeError(error, 'The upload was refused'));
    parts.push(sent > 0
      ? `${sent} of ${total} uploaded. ${why} ${remaining === 1 ? 'The last one was' : `The other ${remaining} were`} not sent — pick ${remaining === 1 ? 'it' : 'them'} again to retry.`
      : `Nothing was uploaded. ${why}`);
  }

  if (oversized.length) {
    const names = oversized.map(nameOf).join(', ');
    parts.push(oversized.length === 1
      ? `${names} is over ${mb(MAX_BYTES_PER_FILE)} and was not sent.`
      : `${oversized.length} files are over ${mb(MAX_BYTES_PER_FILE)} and were not sent: ${names}.`);
  }

  if (!parts.length) return null;
  return parts.join(' ');
}

/**
 * The same question for the ONE-REQUEST-PER-PHOTO path (apps/field).
 *
 * Its outcome has a different shape — `{ sent, queued, refused }`, because a
 * photo taken with no signal is kept rather than sent — but the rule is the
 * same one, and it lives here so the two paths cannot word it differently:
 * never let a partial upload read as a success, and always name a count.
 *
 * `queued` counts toward "added" on purpose. To a crew member a photo held on
 * the phone for replay is done — it is in the app and it will go up. Only a
 * REFUSAL is something they have to act on.
 */
export function photoOutcome({ sent = 0, queued = 0, refused = [] } = {}) {
  if (!refused.length) return null;
  const done = sent + queued;
  const total = done + refused.length;
  const names = refused.map(nameOf).join(', ');
  const why = endSentence(describeError(refused[0].error, 'It was refused'));
  if (done === 0) return `None of the ${total} were added. ${why}`;
  return refused.length === 1
    ? `${done} of ${total} added. ${names} could not be: ${why}`
    : `${done} of ${total} added. ${refused.length} could not be (${names}): ${why}`;
}
